import { signal, computed, WritableSignal } from '@angular/core';
import { Subject } from 'rxjs';
import { IBaseQueryParams } from './crud.interfaces';

// ============ STATE SIGNALS ============

export interface PageStateSignals<TEntity> {
  _data: WritableSignal<TEntity[]>;
  _totalCount: WritableSignal<number>;
  _isLoading: WritableSignal<boolean>;
  _error: WritableSignal<string | null>;
}

export function createPageStateSignals<TEntity>(): PageStateSignals<TEntity> {
  return {
    _data: signal<TEntity[]>([]),
    _totalCount: signal<number>(0),
    _isLoading: signal<boolean>(false),
    _error: signal<string | null>(null)
  };
}

export function createPageStateComputed<TEntity>(state: PageStateSignals<TEntity>) {
  return {
    data: computed(() => state._data()),
    totalCount: computed(() => state._totalCount()),
    isLoading: computed(() => state._isLoading()),
    error: computed(() => state._error())
  };
}

// ============ ACTION SUBJECTS ============

export function createPageActionSubjects() {
  return {
    setSearch$: new Subject<string>(),
    setPagination$: new Subject<{ pageNum: number; pageSize: number }>(),
    setSort$: new Subject<{ column: string | null; direction: 'ASC' | 'DESC' | null }>(),
    reload$: new Subject<void>()
  };
}

// ============ QUERY PARAMS UTILITIES ============

export interface ParseQueryParamsConfig<TQueryParams extends IBaseQueryParams> {
  defaults: TQueryParams;
  parseCustomFields?: (params: Record<string, string>) => Partial<TQueryParams>;
}

export function parseQueryParams<TQueryParams extends IBaseQueryParams>(
  params: Record<string, string>,
  config: ParseQueryParamsConfig<TQueryParams>
): TQueryParams {
  const base: IBaseQueryParams = {
    search: params?.['search'] ?? '',
    pageNum: params?.['pageNum'] ? parseInt(params['pageNum'], 10) : config.defaults.pageNum,
    pageSize: params?.['pageSize'] ? parseInt(params['pageSize'], 10) : config.defaults.pageSize,
    sortBy: params?.['sortBy'] ?? config.defaults.sortBy,
    sortOrder: (params?.['sortOrder'] as 'ASC' | 'DESC') ?? config.defaults.sortOrder
  };

  const custom = config.parseCustomFields?.(params) ?? {};

  return { ...config.defaults, ...base, ...custom } as TQueryParams;
}

export interface CleanQueryParamsConfig<TQueryParams extends IBaseQueryParams> {
  defaults: TQueryParams;
  cleanCustomFields?: (params: TQueryParams) => Record<string, any>;
}

export function cleanQueryParams<TQueryParams extends IBaseQueryParams>(
  params: TQueryParams,
  config: CleanQueryParamsConfig<TQueryParams>
): Record<string, any> {
  const cleanParams: Record<string, any> = {};

  if (params.search) cleanParams['search'] = params.search;
  if (params.pageNum !== config.defaults.pageNum) cleanParams['pageNum'] = params.pageNum;
  if (params.pageSize !== config.defaults.pageSize) cleanParams['pageSize'] = params.pageSize;
  if (params.sortBy) cleanParams['sortBy'] = params.sortBy;
  if (params.sortOrder) cleanParams['sortOrder'] = params.sortOrder;

  const custom = config.cleanCustomFields?.(params) ?? {};

  return { ...cleanParams, ...custom };
}

// ============ DEFAULT QUERY PARAMS ============

export function createDefaultQueryParams(): IBaseQueryParams {
  return {
    search: '',
    pageNum: 1,
    pageSize: 10,
    sortBy: undefined,
    sortOrder: undefined
  };
}
